import {
  isDisposalType,
  adjustToTimeZone,
  SiteEntity,
  siteStatisticsSelector,
  siteType,
  StatisticsSearchCriteria,
  StatisticsType,
  statisticsType,
  transportCategory,
  TransportCategory,
  transportMethod,
  useSearchStatisticsQuery
} from "@ots-frontend/ots-data";
import {useCallback, useMemo, useState} from "react";
import {useDispatch} from "react-redux";
import {useOtsUISelector} from "../../../hooks/useOtsUISelector";
import {skipToken} from "@reduxjs/toolkit/query";
import {selectSiteUIFilter} from "../../../store/entity/site/selectors";
import {setUISiteFilter} from "../../../store/entity/site/actions";
import {EntityInsightsView} from "../common/EntityInsightsView/EntityInsightsView";
import {Divider, Group, Radio, Space, Tabs, Title} from "@mantine/core";
import {useLocation, useNavigate} from "react-router-dom";
import {TruckingInsightsGrid} from "../../insights/TruckingInsightsGrid/TruckingInsightsGrid";
import {PipelineInsightsGrid} from "../../insights/PipelineInsightsGrid/PipelineInsightsGrid";
import { StatisticsDataTable } from "../../data-table/StatisticsDataTable/StatisticsDataTable";
import { useAuth } from "../../../providers/AuthProvider/AuthProvider";

export type SiteInsightsProps = {
  site: SiteEntity
  category?: TransportCategory
  regionId?: string
}

export const SiteInsights = (props: SiteInsightsProps) => {
  const {site, category, regionId,} = props;

  const { hasAccess } = useAuth()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {pathname, search, state} = useLocation();

  const filter = useOtsUISelector(state => selectSiteUIFilter(state, site.id))

  const [statsAs, setStatsAs] = useState<string>(isDisposalType(site.type) ? "delivery" : "pickup");

  const criteria: StatisticsSearchCriteria | any = useMemo(() => {
    if (filter && regionId) {
      return {
        fromDttm: adjustToTimeZone(filter.fromDttm).getTime(),
        toDttm: adjustToTimeZone(filter.toDttm).getTime(),
        regionIds: [regionId],
        siteIds: [site.id],
        includeSubTruckCompanies: true,
        includeTruckingData: true,
        includePipelineData: true,
        categories: category ? [category.code] : undefined,
      }
    } else {
      return skipToken
    }
  }, [filter, regionId, site.id, category])

  const selectStatistics = useMemo(() => {
    return siteStatisticsSelector()
  }, []);

  const {stats, isLoading} = useSearchStatisticsQuery(criteria, {
    selectFromResult: (result) => ({
      ...result,
      stats: selectStatistics(result, site.id),
      isLoading: result.isLoading || result.isFetching,
    })
  });

  const onFilterChange = useCallback((fromDate: Date, toDate: Date | undefined) => {
    dispatch(setUISiteFilter({
      id: site.id,
      fromDttm: fromDate.getTime(),
      toDttm: toDate?.getTime()
    }));
  }, [dispatch, site.id]);

  const onStatsAsChange = useCallback((value: string) => {
    setStatsAs(value)
  }, [])

  const visibleStats = useMemo(() => {
    return statsAs === 'pickup' ? stats?.pickup : stats?.delivery
  }, [statsAs, stats])

  const truckingRateModel = useMemo(() => {
    return statsAs === 'pickup' ? "Hauling" : "Disposal"
  }, [statsAs])

  const siteToSiteTruckingRateModel = useMemo(() => {
    return statsAs === 'pickup' ? "Disposal" : "Hauling"
  }, [statsAs])

  const truckingStatisticsTypes = useMemo(() => {
    const types: StatisticsType[] = [statisticsType.loads, statisticsType.quantity]
    if (category === transportCategory.oil) {
      types.push(statisticsType.rejects)
    }

    types.push(statisticsType.avgTurnaroundTime)

    if (hasAccess("view_cost")) {
      types.push(statisticsType.cost)
      types.push(statisticsType.costPerQuantity)
    }

    return types
  }, [category, hasAccess])

  const pipelineStatisticsTypes = useMemo(() => {
    const types: StatisticsType[] = [statisticsType.quantity]

    if (hasAccess("view_cost")) {
      types.push(statisticsType.cost)
      types.push(statisticsType.costPerQuantity)
    }

    return types
  }, [category, hasAccess])

  return (
    <EntityInsightsView
      onSubmit={onFilterChange}
      form={{
        defaultValues: {
          fromDate: filter ? new Date(filter.fromDttm) : new Date(),
          toDate: filter?.toDttm ? new Date(filter.toDttm) : undefined,
        }
      }}>
      <Tabs
        defaultValue={state?.insightsTabValue ?? "trucking"}
        onTabChange={(value) => navigate(pathname + search, {
          replace: true,
          state: {...state, insightsTabValue: value}
        })}
      >
        <Tabs.List>
          <Tabs.Tab value={"trucking"}>
            {"Trucking"}
          </Tabs.Tab>
          <Tabs.Tab value={"pipeline"}>
            {"Pipeline"}
          </Tabs.Tab>
        </Tabs.List>
        <Space h="md"/>
        <Tabs.Panel value="trucking">
          <Radio.Group onChange={onStatsAsChange} value={statsAs}>
            <Group mt="xs">
              <Radio value="pickup" label="Pickup"/>
              <Radio value="delivery" label="Delivery"/>
            </Group>
          </Radio.Group>
          <Space h="md"/>
          <TruckingInsightsGrid
            insights={visibleStats}
            loading={isLoading}
            rateModels={[truckingRateModel]}/>
          <Divider m="md"/>
          <Title order={5}>Site to Site</Title>
          <StatisticsDataTable
            withBorder={true}
            withColumnBorders={true}
            scrollAreaProps={{
              offsetScrollbars: true
            }}
            mt="md"
            method={transportMethod.trucking}
            types={truckingStatisticsTypes}
            rateModel={siteToSiteTruckingRateModel}
            category={category}
            records={visibleStats ? Object.values(visibleStats.reverseSiteStatMap) : undefined}
            recordsPerPageOptions={[10]}
            fetching={isLoading}
            hideExpansion={true}
            showTotals={true}/>
        </Tabs.Panel>
        <Tabs.Panel value="pipeline">
          <Radio.Group onChange={onStatsAsChange} value={statsAs}>
            <Group mt="xs">
              <Radio value="pickup" label="Pickup"/>
              <Radio value="delivery" label="Delivery"/>
            </Group>
          </Radio.Group>
          <Space h="md"/>
          <PipelineInsightsGrid
            insights={visibleStats}
            loading={isLoading}
            rateModels={['Pipeline']}/>
          <Divider m="md"/>
          <Title order={5}>Site to Site</Title>
          <StatisticsDataTable
            withBorder={true}
            withColumnBorders={true}
            scrollAreaProps={{
              offsetScrollbars: true
            }}
            mt="md"
            method={transportMethod.pipeline}
            types={pipelineStatisticsTypes}
            rateModel={'Pipeline'}
            category={category}
            records={visibleStats ? Object.values(visibleStats.reverseSiteStatMap) : undefined}
            recordsPerPageOptions={[10]}
            fetching={isLoading}
            hideExpansion={true}
            showTotals={true}/>
        </Tabs.Panel>
      </Tabs>
    </EntityInsightsView>
  );
};

